import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { GALLERY_CATEGORIES } from "../data/content";
import { usePortfolio } from "../lib/usePortfolio";
import Lightbox from "./Lightbox";
import Slideshow from "./Slideshow";
import { Play } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export default function Gallery() {
  const { gallery: GALLERY } = usePortfolio();
  const [active, setActive] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [slideshowOpen, setSlideshowOpen] = useState(false);

  const filtered = useMemo(
    () => (active === "All" ? GALLERY : GALLERY.filter((g) => g.category === active)),
    [active, GALLERY]
  );

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-28 md:py-40 bg-[#0D0D10]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.header
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <p className="text-[11px] font-mono tracking-[0.25em] uppercase text-stone-500">
            02 // The Archive
          </p>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-ivory">
              Selected Frames, <span className="italic text-champagne">Unrepeatable Light</span>
            </h2>
            <div className="flex items-center gap-5">
              <span data-testid="gallery-count" className="text-[11px] font-mono tracking-[0.2em] uppercase text-stone-500">
                {filtered.length} Frames
              </span>
              <button
                data-testid="gallery-slideshow-btn"
                onClick={() => setSlideshowOpen(true)}
                className="inline-flex items-center gap-2 border border-champagne/40 px-5 py-2 text-[11px] font-mono tracking-[0.2em] uppercase text-champagne hover:bg-champagne hover:text-obsidian transition-colors duration-300"
              >
                <Play size={12} />
                Play Slideshow
              </button>
            </div>
          </div>
        </motion.header>

        <div className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label="Gallery categories">
          {GALLERY_CATEGORIES.map((category) => {
            const isActive = active === category;
            return (
              <button
                key={category}
                data-testid={`gallery-filter-${category.toLowerCase()}`}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(category)}
                className={`px-5 py-2 text-[11px] font-mono tracking-[0.2em] uppercase border transition-colors duration-300 ${
                  isActive
                    ? "border-gold/60 bg-gold/10 text-champagne"
                    : "border-white/10 text-stone-400 hover:border-champagne/40 hover:text-champagne"
                }`}
              >
                {category}
              </button>
            );
          })}
        </div>

        <motion.div layout className="mt-12 grid grid-cols-12 gap-4 md:gap-5" data-testid="gallery-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((item, i) => (
              <motion.figure
                layout
                key={item.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.45, ease: EASE }}
                data-testid={`gallery-item-${item.id}`}
                onClick={() => setLightboxIndex(i)}
                className={`group relative col-span-12 cursor-pointer overflow-hidden bg-surface ${item.span}`}
              >
                <div className={`${item.aspect} overflow-hidden`}>
                  <img
                    src={item.url}
                    alt={item.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-all duration-700 ease-out group-hover:scale-105 group-hover:contrast-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-champagne">
                    {item.category} — {item.location}
                  </p>
                  <h3 className="mt-1 font-serif text-lg text-ivory">{item.title}</h3>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox
            images={filtered}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onNavigate={setLightboxIndex}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {slideshowOpen && (
          <Slideshow images={filtered} onClose={() => setSlideshowOpen(false)} />
        )}
      </AnimatePresence>
    </section>
  );
}
